import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { isAfter, isBefore, isValid as isValidDate, format } from 'date-fns';
import { array, number, object, string } from 'yup';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

import { Box, Button, Form } from '@base-app';
import { COMMON_TRANSLATION } from '@/common/constants';
import { DATE, END_DATE, EditContentTableProps, START_DATE } from './EditContentTable.types';
import { formatDate } from '../EditContent.types';
import { getFirstDayOfYear, getLastDayOfYear, maskNumber } from '@/common/utils';
import { SelectDateRangeModal } from '@builder';
import { SequenceGoal } from '../../SequenceGoal/SequenceGoal.types';
import { STUDY_INSIGHTS_TRANSLATION, STUDY_YEAR_LIMITS, TARGET_GOAL_VALUE } from '@modules/study-insights/constants';
import {
  StyledAddRowContainer,
  StyledButtonContainer,
  StyledCalendarIcon,
  StyledContent,
  StyledContentContainer,
  StyledDeleteLink,
  StyledEditButton,
  StyledEditContentTable,
  StyledEditedRow,
  StyledErroIcon,
  StyledErrorContainer,
  StyledInput,
  StyledRow,
  StyledRowTitle,
  StyledTitle,
  StyledTitleContainer,
  StyleRowData,
} from './EditContentTable.styles';
import { Variants } from '@common/types';

const EditContentTable: FC<EditContentTableProps> = ({ goals, onSubmit }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);

  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [handleGoals, setHandleGoals] = useState<SequenceGoal[]>(goals);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [originalGoals, setOriginalGoals] = useState<SequenceGoal[]>(goals);
  const [selectedDateIndex, setSelectedDateIndex] = useState<number>(0);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);

  const schema = object()
    .shape({
      data: array().of(
        object().shape({
          endDate: string().required(),
          startDate: string().required(),
          target: number()
            .nullable()
            .required(t('edit_goals.required'))
            .typeError(t('form.errors.number.type', { ns: COMMON_TRANSLATION }))
            .min(
              TARGET_GOAL_VALUE.MIN,
              t('form.errors.number.min', {
                ns: COMMON_TRANSLATION,
                min: Number(TARGET_GOAL_VALUE.MIN).toLocaleString(),
              }),
            )
            .max(
              TARGET_GOAL_VALUE.MAX,
              t('form.errors.number.max', {
                ns: COMMON_TRANSLATION,
                max: Number(TARGET_GOAL_VALUE.MAX).toLocaleString(),
              }),
            )
            .transform((_, originalValue) => {
              const currentValue = originalValue ? originalValue.toString().replace(/,/g, '') : '';

              if (originalValue === '' || originalValue === null) {
                return null;
              }

              return Number(currentValue);
            }),
        }),
      ),
    })
    .required();

  const {
    control,
    formState: { isSubmitting, isValid, errors },
    clearErrors,
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      data: goals,
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'data',
  });

  const getFormattedDate = (date: string): string => {
    if (!date) {
      return '';
    }

    return format(new Date(date), formatDate);
  };

  const toggleEdit = (): void => {
    setOriginalGoals(fields);
    setIsEditing(true);

    setDefaultValues();
  };

  const onCancelEdit = (): void => {
    setIsEditing(false);
    setErrorMessage(null);
    reset({ data: originalGoals });
    setHandleGoals(originalGoals);
  };

  const handleChangeTarget = (value: string, index: number): void => {
    const newGoals = cloneDeep(handleGoals);
    newGoals[index].target = Number(value.toString().replace(/,/g, ''));

    setHandleGoals(newGoals);
  };

  const handleOnChangeDate = (value: string, key: keyof SequenceGoal, index: number): void => {
    setSelectedDateIndex(index);
    const newGoals = cloneDeep(handleGoals);
    const rxDatePattern = /^(\d{1,2})(\/)(\d{1,2})(\/)(\d{2,4})$/;

    if (rxDatePattern.test(value)) {
      (newGoals[index][key] as string) = new Date(value).toISOString();
      setHandleGoals(newGoals);
      setErrorMessage(null);
      clearErrors();
    } else {
      setErrorMessage(t('edit_goals.not_valid_date'));
    }
  };

  const checkDates = (): boolean => {
    let hasError = false;
    let localErrorMessage: string | null = null;

    handleGoals.forEach((_, index) => {
      const end = handleGoals[index].endDate;
      const start = handleGoals[index].startDate;

      const endDate = new Date(end);
      const startDate = new Date(start);

      const isValidEndDate = end !== '' && isValidDate(endDate);
      const isValidStartDate = start !== '' && isValidDate(startDate);

      if (start !== '' && !isValidStartDate) {
        localErrorMessage = t('edit_goals.not_valid_date');
        hasError = true;
      }

      if (end !== '' && !isValidEndDate) {
        localErrorMessage = t('edit_goals.not_valid_date');
        hasError = true;
      }

      if (hasError) return;

      // Check overlap with previous row - only if both current start and previous end are valid
      // Allow dates to "touch" (startDate === prevEndDate) but reject actual overlaps (startDate < prevEndDate)
      if (index !== 0) {
        const prevEnd = handleGoals[index - 1].endDate;
        const prevEndDate = new Date(prevEnd);
        const isPrevEndValid = prevEnd !== '' && isValidDate(prevEndDate);

        if (isValidStartDate && isPrevEndValid && isBefore(startDate, prevEndDate)) {
          localErrorMessage = t('edit_goals.overlap');
          hasError = true;
        }
      }

      const isDateBefore =
        (end !== '' && isBefore(endDate, new Date(DATE.MIN))) ||
        (start !== '' && isBefore(startDate, new Date(DATE.MIN)));
      const isDateAfter =
        (end !== '' && isAfter(endDate, new Date(DATE.MAX))) ||
        (start !== '' && isAfter(startDate, new Date(DATE.MAX)));

      if (isDateBefore) {
        localErrorMessage = t('edit_goals.min_date', { date: DATE.MIN });
        hasError = true;
      }

      if (isDateAfter) {
        localErrorMessage = t('edit_goals.max_date', { date: DATE.MAX });
        hasError = true;
      }

      if (start !== '' && end !== '' && isBefore(endDate, startDate)) {
        localErrorMessage = t('edit_goals.overlap');
        hasError = true;
      }
    });

    setErrorMessage(localErrorMessage);
    return !hasError;
  };

  const handleOpenCalendar = (startDate: string, endDate: string, id: number): void => {
    setSelectedStartDate(startDate ? new Date(startDate) : null);
    setSelectedEndDate(endDate ? new Date(endDate) : null);
    setSelectedDateIndex(id);
    setIsCalendarOpen(true);
  };

  const handleCloseCalendar = (): void => {
    setIsCalendarOpen(false);
  };

  const handleApplyChangeDate = (newDate: any): void => {
    const newGoals = cloneDeep(handleGoals);
    newGoals[selectedDateIndex].endDate = newDate.toDate.toISOString();
    newGoals[selectedDateIndex].startDate = newDate.fromDate.toISOString();

    reset({ data: newGoals });
    setHandleGoals(newGoals);
    handleCloseCalendar();
  };

  const handleSubmitForm = (): void => {
    if (isSubmitting) {
      return;
    }

    if (!errorMessage && checkDates()) {
      onSubmit(handleGoals);
    }
  };

  const setDefaultValues = (): void => {
    const formValues = cloneDeep(handleGoals);
    const newGoals = cloneDeep(handleGoals);
    handleGoals.forEach((goal, index) => {
      if (!goal.startDate) {
        const defaultValue = getDefaultDateByIndex(index, true).toISOString();
        formValues[index].startDate = defaultValue;
        newGoals[index].startDate = defaultValue;
      }
      if (!goal.endDate) {
        const defaultValue = getDefaultDateByIndex(index, false).toISOString();
        formValues[index].endDate = defaultValue;
        newGoals[index].endDate = defaultValue;
      }
      // Set default target for goals without a target to ensure form validity
      // Using 0 as default since it's the minimum valid value and displays as empty in the input
      if (goal.target === null || goal.target === undefined) {
        formValues[index].target = TARGET_GOAL_VALUE.MIN;
        newGoals[index].target = TARGET_GOAL_VALUE.MIN;
      }
    });

    reset({ data: formValues });
    setHandleGoals(newGoals);
  };

  const getDefaultDateByIndex = (index: number, isStartDate = true): Date => {
    const currDate = new Date();
    currDate.setFullYear(currDate.getFullYear() + index);

    const currDateString = isStartDate ? getFirstDayOfYear(currDate) : getLastDayOfYear(currDate);

    return currDateString;
  };

  const handleAddRow = (): void => {
    if (fields.length >= STUDY_YEAR_LIMITS.MAX) return;

    const newYear: SequenceGoal = {
      startDate: '',
      endDate: '',
      target: null as any,
      value: null as any,
    };

    append(newYear);
    setHandleGoals((prev) => [...prev, newYear]);
  };

  const handleDeleteRow = (index: number): void => {
    if (fields.length <= STUDY_YEAR_LIMITS.MIN) return;

    // Close calendar if open to prevent stale state
    if (isCalendarOpen) {
      setIsCalendarOpen(false);
    }

    // Update selectedDateIndex to prevent out-of-bounds access
    const newLength = handleGoals.length - 1;
    if (selectedDateIndex >= newLength) {
      setSelectedDateIndex(Math.max(0, newLength - 1));
    } else if (index < selectedDateIndex) {
      setSelectedDateIndex(selectedDateIndex - 1);
    }

    remove(index);
    setHandleGoals((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    checkDates();
  }, [handleGoals]);

  return (
    <StyledEditContentTable data-testid={'edit-content-tablet'}>
      <StyledTitleContainer>
        <StyledTitle>{t('edit_goals.yearly_sequencing_goals')}</StyledTitle>
        <StyledEditButton
          data-testid={isEditing ? 'edit-content-tablet-cancel' : 'edit-content-tablet-edit'}
          onClick={isEditing ? onCancelEdit : toggleEdit}
        >
          {isEditing ? t('edit_goals.cancel') : t('edit_goals.edit')}
        </StyledEditButton>
      </StyledTitleContainer>
      <StyledContentContainer>
        {errorMessage && (
          <StyledErrorContainer>
            <Box css={{ marginRight: '$4' }}>{<StyledErroIcon />}</Box>
            <Box>{errorMessage}</Box>
          </StyledErrorContainer>
        )}
        <Form
          onSubmit={handleSubmit(handleSubmitForm)}
          css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
          {fields?.map((field, index) => {
            const startDate = getFormattedDate(field.startDate);
            const endDate = getFormattedDate(field.endDate);

            return (
              <StyledContent key={field.id}>
                <StyledRow css={{ marginRight: '$8' }}>
                  <StyledRowTitle>{t('edit_goals.yearly_total_sequencing_goals', { total: index + 1 })}</StyledRowTitle>
                  {!isEditing && (
                    <StyleRowData data-testid={`edit-content-table-target-value-${index}`}>
                      {typeof field.target === 'number'
                        ? Number(field.target).toLocaleString('en')
                        : t('edit_goals.not_yet_defined')}
                    </StyleRowData>
                  )}
                  {isEditing && (
                    <Controller
                      control={control}
                      defaultValue={field.target}
                      name={`data.${index}.target`}
                      rules={{ required: true, min: TARGET_GOAL_VALUE.MIN, max: TARGET_GOAL_VALUE.MAX }}
                      render={({ field }) => {
                        return (
                          <StyledInput
                            {...field}
                            css={{ backgroundColor: '$white', width: '100%' }}
                            error={!(errors.data?.[index]?.target == null)}
                            errorDescription={errors.data?.[index]?.target?.message as string}
                            id={field.name}
                            label={''}
                            name={field.name}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                              const {
                                target: { value },
                              } = event;

                              handleChangeTarget(value, index);
                              field.onChange(event);
                              setValue(field.name, maskNumber(value) as any);
                            }}
                            placeholder={''}
                            testId={`edit-content-table-target-value-input-${index}`}
                            role="target-value-input"
                            type="string"
                            value={field.value ? maskNumber(field.value) : ''}
                            variant={Variants.PRIMARY}
                          />
                        );
                      }}
                    />
                  )}
                </StyledRow>
                <StyledRow css={{ width: '100%', position: 'relative' }}>
                  <StyledRowTitle>{t('edit_goals.year_time_range', { total: index + 1 })}</StyledRowTitle>
                  {!isEditing && (
                    <StyleRowData data-testid={`edit-content-table-start-date-value-${index}`}>
                      {startDate !== ''
                        ? `${startDate} - ${endDate}`
                        : `${t('edit_goals.format_date')} - ${t('edit_goals.format_date')}`}
                    </StyleRowData>
                  )}
                  {isEditing && (
                    <StyledEditedRow>
                      <Box css={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <StyleRowData css={{ borderBottom: '$borderWidths$1 solid $darkGray700' }}>
                          {t('edit_goals.start_date')}
                        </StyleRowData>
                        <Controller
                          control={control}
                          defaultValue={startDate}
                          name={`data.${index}.startDate`}
                          render={({ field }) => {
                            return (
                              <StyledInput
                                {...field}
                                css={{ backgroundColor: '$white', width: '100%' }}
                                id={field.name}
                                label={''}
                                name={field.name}
                                placeholder={startDate || t('edit_goals.format_date')}
                                testId={`edit-content-table-start-date-value-input-${index}`}
                                type="string"
                                value={startDate}
                                variant={Variants.PRIMARY}
                                onBlur={checkDates}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                  const {
                                    target: { value },
                                  } = event;

                                  handleOnChangeDate(value, START_DATE, index);
                                  field.onChange(event);
                                }}
                              />
                            );
                          }}
                        />
                        <StyleRowData
                          css={{ borderBottom: '$borderWidths$1 solid $darkGray700' }}
                          data-testid={'edit-content-table-end-date-value'}
                        >
                          {t('edit_goals.end_date')}
                        </StyleRowData>
                        <Controller
                          control={control}
                          defaultValue={endDate}
                          name={`data.${index}.endDate`}
                          render={({ field }) => {
                            return (
                              <StyledInput
                                {...field}
                                css={{ backgroundColor: '$white', width: '100%' }}
                                id={field.name}
                                label={''}
                                name={field.name}
                                placeholder={endDate || t('edit_goals.format_date')}
                                testId={`edit-content-table-end-date-value-input-${index}`}
                                type="string"
                                value={endDate}
                                variant={Variants.PRIMARY}
                                onBlur={checkDates}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                  const {
                                    target: { value },
                                  } = event;

                                  handleOnChangeDate(value, END_DATE, index);
                                  field.onChange(event);
                                }}
                              />
                            );
                          }}
                        />
                        <Box
                          as="button"
                          type="button"
                          aria-label={t('edit_goals.open_date_selector')}
                          css={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            '&:focus': {
                              outline: '2px solid $blue400',
                              outlineOffset: '2px',
                            },
                          }}
                          onClick={() =>
                            handleOpenCalendar(handleGoals[index].startDate, handleGoals[index].endDate, index)
                          }
                        >
                          <StyledCalendarIcon />
                        </Box>
                      </Box>
                      {fields.length > STUDY_YEAR_LIMITS.MIN && (
                        <StyledDeleteLink
                          as="button"
                          type="button"
                          aria-label={t('edit_goals.delete_row', { year: index + 1 })}
                          onClick={() => handleDeleteRow(index)}
                          data-testid={`delete-row-button-${index}`}
                        >
                          {t('edit_goals.delete')}
                        </StyledDeleteLink>
                      )}
                    </StyledEditedRow>
                  )}
                </StyledRow>
              </StyledContent>
            );
          })}
          {isEditing && fields.length < STUDY_YEAR_LIMITS.MAX && (
            <StyledAddRowContainer>
              <Button
                variant={Variants.SECONDARY}
                css={{ padding: '$1 $3' }}
                onClick={handleAddRow}
                id="add-row-button"
              >
                {t('edit_goals.add_row')}
              </Button>
            </StyledAddRowContainer>
          )}
          {isEditing && (
            <StyledButtonContainer>
              <Button disabled={!isValid || !!errorMessage} id="edit-content-tablet-submit" type="submit">
                {t('edit_goals.save')}
              </Button>
            </StyledButtonContainer>
          )}
        </Form>
        <SelectDateRangeModal
          endDate={selectedEndDate}
          isOpen={isCalendarOpen}
          maxDate={new Date(DATE.MAX)}
          minDate={new Date(DATE.MIN)}
          onSubmit={(newDate) => handleApplyChangeDate(newDate)}
          startDate={selectedStartDate}
          toggle={handleCloseCalendar}
        />
      </StyledContentContainer>
    </StyledEditContentTable>
  );
};

export default EditContentTable;
